import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, X } from "lucide-react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { API } from "../lib/helpers";

/**
 * Elite upsell banner — ONLY shows when the backend says the current Pro
 * user's usage signals indicate intent (3+ unlock clicks, 10+ analyses,
 * cap reached, or 7-day return). Never blocks — "Maybe later" dismisses
 * for 7 days via localStorage.
 */
const DISMISS_KEY = "elite_upsell_dismissed_until";
const DISMISS_TRIGGER_KEY = "elite_upsell_dismissed_trigger";
const DISMISS_DAYS = 7;

const TRIGGER_COPY = {
  multiple_unlocks: "You've unlocked verified rent 3+ times.",
  cap_reached: "You've hit your monthly RentCast limit.",
  heavy_analysis: "You've analyzed 10+ properties.",
  returning_user: "Welcome back — let's make this faster.",
};

export default function EliteUpsellBanner({ className = "" }) {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // Honor a local 7-day dismiss — UNLESS a new (different) trigger has fired
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      setDismissed(Date.now() < until);
    } catch (_) { /* ignore */ }
  }, []);

  // Check eligibility from backend. If the current trigger differs from the
  // one the user dismissed, reset the dismissal (they've hit a NEW signal).
  useEffect(() => {
    axios
      .get(`${API}/elite-upsell/eligibility`)
      .then((r) => {
        const data = r.data;
        setEligibility(data);
        try {
          if (data?.eligible) {
            const lastTrigger = localStorage.getItem(DISMISS_TRIGGER_KEY);
            if (lastTrigger && lastTrigger !== data.trigger) {
              // NEW trigger — clear dismissal and show again.
              localStorage.removeItem(DISMISS_KEY);
              localStorage.removeItem(DISMISS_TRIGGER_KEY);
              setDismissed(false);
            }
          }
        } catch (_) { /* ignore */ }
      })
      .catch(() => setEligibility({ eligible: false }));
  }, []);

  // Fire viewed event once per session (avoids React StrictMode double-fire)
  useEffect(() => {
    if (!eligibility?.eligible || dismissed) return;
    const key = "elite_upsell_viewed_session";
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch (_) { /* ignore */ }
    axios
      .post(`${API}/analytics/track`, {
        event_type: "elite_upsell_viewed",
        metadata: {
          trigger: eligibility.trigger,
          signals: eligibility.signals,
        },
      })
      .catch(() => {});
  }, [eligibility, dismissed]);

  const handleUpgrade = useCallback(() => {
    axios
      .post(`${API}/analytics/track`, {
        event_type: "elite_upsell_clicked",
        metadata: { trigger: eligibility?.trigger },
      })
      .catch(() => {});
    navigate("/billing?tier=elite_monthly");
  }, [navigate, eligibility]);

  const handleDismiss = useCallback(() => {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
      if (eligibility?.trigger) {
        localStorage.setItem(DISMISS_TRIGGER_KEY, eligibility.trigger);
      }
    } catch (_) { /* ignore */ }
    setDismissed(true);
  }, [eligibility]);

  if (!eligibility?.eligible || dismissed) return null;

  const triggerText = TRIGGER_COPY[eligibility.trigger] || "";

  return (
    <Card
      className={`relative overflow-hidden bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border border-amber-500/40 p-4 sm:p-5 ${className}`}
      data-testid="elite-upsell-banner"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        data-testid="elite-upsell-dismiss"
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors p-1"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex-shrink-0 p-3 rounded-full bg-amber-500/15">
          <Crown className="w-6 h-6 text-amber-500" />
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-500">
              Skip unlock clicks with Elite
            </span>
            {triggerText && (
              <span className="text-[10px] text-muted-foreground opacity-80">
                · {triggerText}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground leading-snug max-w-2xl">
            Elite automatically loads verified rent on every analysis, so you
            can move faster on serious deals.
          </p>
        </div>

        <div className="flex gap-2 flex-shrink-0 w-full sm:w-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            data-testid="elite-upsell-maybe-later"
            className="text-muted-foreground hover:text-foreground"
          >
            Maybe later
          </Button>
          <Button
            size="sm"
            onClick={handleUpgrade}
            data-testid="elite-upsell-upgrade"
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
          >
            Upgrade to Elite
          </Button>
        </div>
      </div>
    </Card>
  );
}
